import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {ReactComponent as BackgroundImage} from '../../assests/svg/multi-client-header-bg.svg';
import {t} from 'i18next';
import {resetClientData, setHideClientDropdown} from '../../redux/slices/selectedClientSlice';
import {useDispatch, useSelector} from 'react-redux';
import {
  FULL_DATE_FORMAT,
  POPUP_BUTTON_STYLE,
  PROJECT_DROP_DOWN_ID,
  PROJECT_LIST_SEARCH_ENABLE_COUNT,
} from '../../constants/constants';
import AurClientDropdown from '../client-management/aur-client-dropdown/AurClientDropdown';
import AurPopup from '../common/aur-popup/AurPopup';
import AurClientDropDownWrapper from '../layouts/AurClientDropDownWrapper';
import AurButton from '../common/aur-button/AurButton';
import {AurDropdownButton} from '../common/aur-button/AurDropdownButton';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import {RootState} from '../../redux/store';
import {SingleClientTabStatus} from '../pages/user-management/models';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {isEmpty, unionBy} from 'lodash';
import AurTooltip from '../common/aur-tooltip/AurTooltip';
import {
  getEmbedReportsConfig,
  getPowerBIProjects,
  getPowerBiReportGroups,
  exportPowerBiReport,
} from '../../api/ReportingManagement';
import {
  PowerBiEmbedConfig,
  PowerBiProject,
  PowerBiReportGroup,
} from '../../types/power-bi-reports/power-bi-reports';
import {showErrorToast} from '../../api/utils';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {models, Report} from 'powerbi-client';
import {EventHandler, PowerBIEmbed} from 'powerbi-client-react';
import {
  downloadFile,
  getFilenameFromHeaders,
  getHostCountryCode,
  getUTCDateTimeForDisplay,
  isLowerEnvironment,
} from '../../utils/utils';
import {
  PROJECT_PAGE_SIZE,
  PowerBiExportFormat,
  PowerBiExportFormatType,
  PowerBiReportGroups,
  REFRESHED_TIME,
  ReportKeys,
} from './constant';
import {hideCustodianDetailsExportData} from './hideCustodianDetailsExportData';
import AurTabMenu from '../common/aur-tab-menu/AurTabMenu';
import AurBreadcrumb from '../common/aur-breadcrumb/AurBreadCrumb';
import {ReactComponent as ExportIcon} from '../../assests/svg/export_icon.svg';

export const resolvePowerBiExportFileName = (
  format: PowerBiExportFormatType,
  tabName?: string,
  contentDisposition?: string
): string => {
  const extension = format.toLowerCase();
  const trimmedTabName = tabName?.trim();
  const headerFileName = getFilenameFromHeaders(contentDisposition ?? '');
  const fallbackName = `${
    trimmedTabName ?? 'Business_Intelligence_Dashboard'
  }.${extension}`;

  if (format === PowerBiExportFormat.PDF && trimmedTabName) {
    return `${trimmedTabName}.${extension}`;
  }

  return headerFileName || fallbackName;
};

const PowerBiReportsDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const {showToast} = useToast();

  const reportRef = useRef<Report | null>(null);

  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(true);
  const [showClientSelectionModal, setShowClientSelectionModal] =
    useState(false);
  const [refreshBtnDisabled, setRefreshBtnDisabled] = useState(true);
  const [selectedProject, setSelectedProject] = useState<any>({});

  const [dropDownProjects, setDropDownProjects] = useState<PowerBiProject[]>(
    []
  );
  const [initialProjectListLoading, setInitialProjectListLoading] =
    useState<boolean>(false);
  const [projectListLoading, setProjectListLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [reportEmbedConfig, setReportEmbedConfig] =
    useState<models.IReportEmbedConfiguration | null>(null);
  const [reportEmbedKey, setReportEmbedKey] = useState('');
  const pickedProjectFromDropdown = useRef<PowerBiProject>();
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const currentSearchTerm = useRef<string | undefined>(undefined);
  const [refreshedValue, setRefreshedValue] = useState<string>('');
  const [emptyStateMessage, setEmptyStateMessage] = useState<string>('');
  const defaultHeight = 500;
  const [reportHeight, setReportHeight] = useState(defaultHeight); //500px added min height for the report
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabs, setTabs] = useState<any>([]);
  const [dashboardId, setDasboardId] = useState('');
  const [sourceValue, setSourceValue] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [embedConfigs, setEmbedConfigs] = useState<PowerBiEmbedConfig[]>([]);

  useEffect(() => {
    if (
      !selectedClient?.skipClientSelectionModal &&
      isEmpty(selectedClient?.clientId)
    ) {
      dispatch(setHideClientDropdown(true));
      setShowClientSelectionModal(true);
    }
  }, []);

  useEffect(() => {
    if (selectedClient?.clientId && !showClientSelectionModal) {
      setReportEmbedConfig(null);
      setReportEmbedKey('');
      reportRef.current = null;
      setReportHeight(defaultHeight);
      currentSearchTerm.current = undefined;
      setShowDetailedReport(false);
      setTabs([]);
      setDasboardId('');
      setSelectedProject({});
      setSourceValue('')
      setRefreshBtnDisabled(true);
      pickedProjectFromDropdown.current = undefined;
      setInitialProjectListLoading(true);
      fetchProjects('', 1, true);
    } else {
      setEmptyStateMessage('welcome.noClientText');
    }
  }, [selectedClient, showClientSelectionModal]);

  const fetchProjects = async (
    searchInput?: string,
    page = 1,
    reset = false
  ) => {
    if (page === 1) {
      setProjectListLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      let params = `pageSize=${PROJECT_PAGE_SIZE}&pageNumber=${page}&SearchTerm=${searchInput || ''}`;

      const response = await getPowerBIProjects(
        selectedClient?.clientId,
        params
      );
      const fetchedProjects: PowerBiProject[] =
        response?.data?.data.filter(
          (project: PowerBiProject) =>
            project.projectStatus?.toLowerCase() ===
            SingleClientTabStatus.active
        ) || [];
      setHasMore(fetchedProjects.length === PROJECT_PAGE_SIZE);
      let newData = reset
        ? fetchedProjects
        : unionBy([...dropDownProjects, ...fetchedProjects], 'projectId');

      if (
        !searchInput &&
        page === 1 &&
        pickedProjectFromDropdown.current?.projectId
      ) {
        const filtered = newData.filter(
          (p) => p.projectId !== selectedProject.projectId
        );

        newData = [selectedProject, ...filtered].slice(0, PROJECT_PAGE_SIZE);
      }

      setDropDownProjects(newData);
      if (!searchInput) {
        newData.length > 0
          ? setEmptyStateMessage('powerBiReports.selectAlphaCode')
          : setEmptyStateMessage('powerBiReports.noProjectPermission');
      }
    } catch (error) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    } finally {
      setProjectListLoading(false);
      setIsLoadingMore(false);
      setInitialProjectListLoading(false);
    }
  };

  const loadMoreProjects = () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchProjects(currentSearchTerm.current, nextPage);
  };

  const handleProjectListSearchInput = useCallback(
    (input: string) => {
      currentSearchTerm.current = input;
      setPageNumber(1);
      selectedClient?.clientId && fetchProjects(input, 1, true);
    },
    [selectedProject]
  );

  const onProjectPick = (projectId: string) => {
    const project = dropDownProjects.find(
      (item) => item.projectId === projectId
    );
    setSelectedProject(project);
    pickedProjectFromDropdown.current = project;
    if (selectedProject?.projectId !== projectId) {
      setRefreshBtnDisabled(true);
      loadPowerBiReport();
    }
  };

  const loadPowerBiReport = async () => {
    setIsReportLoading(true);
    // Unmount the iframe so the next token starts a new RLS session.
    setReportEmbedConfig(null);
    setReportEmbedKey('');
    reportRef.current = null;
    setTabs([]);
    setSourceValue('');
    try {
      const reportGroupsResponse = await getPowerBiReportGroups(
        selectedClient?.clientId,
        pickedProjectFromDropdown.current?.projectId!,
        PowerBiReportGroups.CCR,
        getHostCountryCode() // MVP supports CCR only; group code will be passed dynamically based on user selection in future.
      );
      const ccrGroup = (reportGroupsResponse.data.data as PowerBiReportGroup[])
        ?.find((group) => group.groupCode === PowerBiReportGroups.CCR);

      if (!ccrGroup) {
        throw new Error(
          `The ${PowerBiReportGroups.CCR} dashboard report is not available.`
        );
      }

      const dashboardReport = ccrGroup?.reports?.find(
        (report) =>
          report.key === ReportKeys.PROJECT && report.isDashboard
      );

      if (!dashboardReport?.reportId) {
        throw new Error(
          `The ${PowerBiReportGroups.CCR} dashboard report is not available.`
        );
      }

      const tabs = ccrGroup.reports
        ?.filter((report) => !report.isDashboard && report.pageId)
        .sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        )
        .map((report, index) => ({
          index,
          content: <></>,
          header: {
            text: report.name,
          },
          pageId: report.pageId!,
        }));
      setTabs(tabs ?? []);
      setSourceValue(ccrGroup.groupDisplayName);
      getReportsConfig(dashboardReport.reportId);
    } catch (error) {
      setIsReportLoading(false);
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    }
  };

  const getReportsConfig = async (reportId: string) => {
    try {
      const response = await getEmbedReportsConfig(
        reportId,
        selectedClient?.clientId,
        pickedProjectFromDropdown.current?.projectId!,
        isLowerEnvironment() ? undefined : getHostCountryCode()
      );
      setEmbedConfigs(response.data.data);
      const config = response.data.data?.find(
        (report: PowerBiEmbedConfig) => !report.isHidden
      );
      if (!config) return;
 
      const embedConfig = buildEmbedConfig(config);
      setReportEmbedConfig(embedConfig);
      setReportEmbedKey(
        `${pickedProjectFromDropdown.current?.projectId ?? ''}-${embedConfig.accessToken}`
      );
      setDasboardId(config.pageId);
      updateRefreshedValue();
      setRefreshBtnDisabled(false);
      setShowDetailedReport(false);
      setSelectedTab(0);
    } catch (error) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    } finally {
      setIsReportLoading(false);
    }
  };

  const updateRefreshedValue = () => {
    setRefreshedValue(t('powerBiReports.projectSection.refreshedValue'));
    const updatedValue = getUTCDateTimeForDisplay(new Date(), FULL_DATE_FORMAT);
    setTimeout(() => {
      setRefreshedValue(updatedValue);
    }, REFRESHED_TIME);
  };

  const calculateReportHeight = useCallback(async () => {
    const report = reportRef.current;
    if (!report) return;

    const container = document.querySelector(
      '.powerbi-container'
    ) as HTMLElement | null;

    if (!container || container.clientWidth === 0) {
      requestAnimationFrame(() => calculateReportHeight());
      return;
    }

    try {
      const pages = await report.getPages();
      const activePage = pages?.find((p) => p.isActive);
      if (!activePage) return;

      const canvasHeight = activePage.defaultSize?.height ?? 0;
      const canvasWidth = activePage.defaultSize?.width ?? 0;
      if (!canvasWidth) return;

      const scale = container.clientWidth / canvasWidth;
      setReportHeight(Math.ceil(canvasHeight * scale)); 
    } catch (error) {
      console.error('Failed to get Power BI canvas height:', error);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calculateReportHeight);
    return () => window.removeEventListener('resize', calculateReportHeight);
  }, [calculateReportHeight]);

  const handlePageChange = useCallback(
    (event: any) => {
      const newPageId = event.detail?.newPage?.name;

      if (dashboardId && newPageId && newPageId !== dashboardId) {
        setShowDetailedReport(true);
        const matchingTab = tabs.find((tab: any) => tab.pageId === newPageId);
        if (matchingTab) setSelectedTab(matchingTab.index);
      }

      calculateReportHeight();
      hideCustodianDetailsExportData(reportRef.current, event.detail?.newPage);
    },
    [dashboardId, tabs, calculateReportHeight]
  );

  const eventHandlers = useMemo(
    () =>
      new Map<string, EventHandler>([
        [
          'loaded',
          () => {
            calculateReportHeight();
            hideCustodianDetailsExportData(reportRef.current);
          },
        ],
        ['pageChanged', handlePageChange],
      ]),
    [calculateReportHeight, handlePageChange]
  );

  const handleTabChange = (tab: any) => {
    navigateToPage(tab.pageId);
  };

  const breadCrumbItems = [
    {
      label: t('powerBiReports.title'),
      path: '#',
      onClick: () => navigateToPage(dashboardId, {showDetailed: false}),
    },
    {
      label: `${tabs[selectedTab]?.header.text}`,
    },
  ];

  const POWER_BI_SETTINGS = {
    panes: {
      filters: {visible: false},
      pageNavigation: {visible: false},
    },
    background: models.BackgroundType.Transparent,
    layoutType: models.LayoutType.Custom,
    customLayout: {
      displayOption: models.DisplayOption.FitToWidth,
    },
    commands: [
    {
      seeData: {
        displayOption: models.CommandDisplayOption.Hidden,
      },
      insightsAnalysis: {
        displayOption: models.CommandDisplayOption.Hidden,
      },
    },
  ],
  };

  const buildEmbedConfig = (apiConfig: {
    reportId: string;
    embedUrl: string;
    embedToken: string;
    pageId: string;
  }): models.IReportEmbedConfiguration => ({
    type: 'report',
    id: apiConfig.reportId,
    embedUrl: apiConfig.embedUrl,
    tokenType: models.TokenType.Embed,
    accessToken: apiConfig.embedToken,
    pageName: apiConfig.pageId, // initial dashboard page from API
    settings: POWER_BI_SETTINGS,
  });

  const handleExport = async (format: PowerBiExportFormatType) => {
    const pageId = tabs[selectedTab]?.pageId;
    const projectId = pickedProjectFromDropdown.current?.projectId ?? selectedProject?.projectId;
    const reportId = embedConfigs.find((config: PowerBiEmbedConfig) => config.pageId === pageId)?.paginatedReportId || '';
    setIsExporting(true);
    try {
      const response = await exportPowerBiReport(
        reportId,
        selectedClient?.clientId,
        projectId,
        format,
        pageId
      );
      const fileName = resolvePowerBiExportFileName(
        format,
        tabs[selectedTab]?.header?.text,
        response.headers['content-disposition']
      );
      downloadFile(response.data, fileName);
    } catch (error) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    } finally {
      setIsExporting(false);
    }
  };

  const navigateToPage = useCallback(
    async (pageId: string, options?: {showDetailed?: boolean}) => {
      if (options?.showDetailed !== undefined) {
        setShowDetailedReport(options.showDetailed);
      }

      const matchingTab = tabs.find((tab: any) => tab.pageId === pageId);
      if (matchingTab) {
        setSelectedTab(matchingTab.index);
      }

      const report = reportRef.current;
      if (!report) return;

      try {
        await report.setPage(pageId);
        // pageChanged will fire → handlePageChange → calculateReportHeight
      } catch (error) {
        console.error('Failed to switch Power BI page:', error);
      }
    },
    [tabs]
  );

  return (
    <Box className="power-bi-reports-dashboard">
      {showDetailedReport ? (
        <Box className="power-bi-detailed-report">
          <div className="power-bi-export-button">
            <AurBreadcrumb items={breadCrumbItems} />
            <AurDropdownButton
              dropdownRole="dropdown"
              iconType="Other"
              buttonText={t('common.exportReport')}
              classNames="typo-text buttonMedium_size"
              startIcon={<ExportIcon aria-hidden />}
              type="continue"
              size="Medium"
              iconSize="medium"
              iconClassName="client-profile-details-dropdown-icon"
              menuDirectionLeft={false}
              menuItemHeight="large"
              menuItemWidth="mediumSmall"
              menuItemTextSize="large"
              menuClassNames="export-button-drop-down"
              menuItems={[
                {
                  label: t('powerBiReports.exportOption.excel'),
                  onClick: () => handleExport(PowerBiExportFormat.XLSX),
                },
                {
                  label: t('powerBiReports.exportOption.pdf'),
                  onClick: () => handleExport(PowerBiExportFormat.PDF),
                },
              ]}
            />
          </div>
          <div className={`powerbi-reports-tab`}>
            <div className="power-bi-report-tabs-header">
              <div className="power-bi-tab-menu">
                <AurTabMenu
                  headerClasses="typo-text body2_size tab-menu-header"
                  isResponsive={true}
                  width="100%"
                  tabs={tabs}
                  title="report"
                  defaultTab={selectedTab}
                  onTabClick={handleTabChange}
                  contentCss="tab-content"
                  tabHeaderDropdownMenuItemWidth="Xlarge"
                />
              </div>
            </div>
          </div>
        </Box>
      ) : (
        <>
          <Box className="header">
            <Box className="header-items">
              <Typography
                className="typo-text headline1_size"
                data-testid="Power-bi-title"
              >
                {t('powerBiReports.title')}
              </Typography>
            </Box>
            <BackgroundImage className="background-image" aria-hidden="true" />
          </Box>
          <Box className="content">
            <Box className="project-selection-section">
              <Box className="left-section">
                <AurClientDropdown
                  buttonOrTextField={'button'}
                  data={dropDownProjects}
                  buttonLabel={
                    selectedProject?.projectId
                      ? ''
                      : t('powerBiReports.projectSection.projectAlphaCode')
                  }
                  dropDownID={PROJECT_DROP_DOWN_ID}
                  idKey="projectId"
                  displayKey="projectAlphaCode"
                  buttonBorderHighlight={true}
                  selectDefaultFirstItem={false}
                  onSearchInput={handleProjectListSearchInput}
                  searchReqLoading={projectListLoading}
                  enableSearch={
                    isEmpty(currentSearchTerm.current)
                      ? dropDownProjects?.length >=
                        PROJECT_LIST_SEARCH_ENABLE_COUNT
                      : true
                  }
                  onOptionPick={onProjectPick}
                  dataTestId="project-dropdown-button"
                  onLoadMore={loadMoreProjects}
                  isLoadingMore={isLoadingMore}
                  isSortedAlphabetically={false}
                  initialSelectedOptionId={
                    pickedProjectFromDropdown.current?.projectId ??
                    selectedProject?.id
                  }
                  disableDropDown={dropDownProjects.length === 0 ? 'Y' : 'N'}
                />
                <Box className="project-detail">
                  <Typography className="typo-text body3_size mist-1500 label_width">
                    {t('powerBiReports.projectSection.projectLabel')}
                  </Typography>
                  <AurTooltip
                    title={
                      selectedProject?.projectAlphaCode
                        ? `${selectedProject.projectAlphaCode} • ${selectedProject.projectName}`
                        : ''
                    }
                    className="typo-text label4_size"
                    toolTipClassName="typo-text subhead5_size"
                    disableFocusListener
                  >
                    <Typography className="text-truncate">
                      {selectedProject?.projectAlphaCode
                        ? `${selectedProject.projectAlphaCode} • ${selectedProject.projectName}`
                        : ''}
                    </Typography>
                  </AurTooltip>
                </Box>
                <Box className="divider"> | </Box>
                <Box className="project-detail">
                  <Typography className="typo-text body3_size mist-1500 label_width">
                    {t('powerBiReports.projectSection.sourceLabel')}
                  </Typography>
                  <AurTooltip
                    title={
                      selectedProject?.projectAlphaCode
                        ? sourceValue
                        : ''
                    }
                    placement="top"
                    className="typo-text label4_size"
                    toolTipClassName="typo-text subhead5_size"
                    disableFocusListener
                  >
                    <Typography className="text-truncate">
                      {selectedProject?.projectAlphaCode
                        ? sourceValue
                        : ''}
                    </Typography>
                  </AurTooltip>
                </Box>
                <Box className="divider"> | </Box>
                <Box className="project-detail">
                  <Typography className="typo-text body3_size mist-1500 refresh_label_width">
                    {t('powerBiReports.projectSection.refreshedLabel')}
                  </Typography>
                  <AurTooltip
                    title={
                      selectedProject?.projectAlphaCode ? refreshedValue : ''
                    }
                    placement="top"
                    className="typo-text label4_size"
                    toolTipClassName="typo-text subhead5_size"
                    disableFocusListener
                  >
                    <Typography className="text-truncate">
                      {selectedProject?.projectAlphaCode ? refreshedValue : ''}
                    </Typography>
                  </AurTooltip>
                </Box>
              </Box>
              <AurButton
                type={'exit'}
                label={t('common.refresh')}
                disabled={refreshBtnDisabled}
                startIcon={<AurSvgIcon src="images/eca_rerun.svg" />}
                onClick={loadPowerBiReport}
                size="Small"
                ariaLabel={t('powerBiReports.projectSection.refreshedTooltip')}
                title={t('powerBiReports.projectSection.refreshedTooltip')}
              />
            </Box>
            <AurPopup
              open={showClientSelectionModal}
              onOk={() => {
                dispatch(setHideClientDropdown(false));
                setShowClientSelectionModal(false);
              }}
              className={`find-invoice-popup`}
              onCancel={() => {
                window.history.back();
                dispatch(setHideClientDropdown(false));
                dispatch(resetClientData());
              }}
              buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
              popupHeading={t('powerBiReports.clientDropdown.heading')}
              showPopupHeading={true}
              contentType="element"
              labelActionCancel={t('buttons.cancel')}
              labelActionOk={t('buttons.continue')}
              showCloseIcon={false}
              showPopupTitle={true}
              popupTitle={t('powerBiReports.clientDropdown.title')}
              continueButtonState={continueButtonDisabled}
              exitButtonHint={t('viewInvoices.projectSelectPopUp.cancelHint')}
              continueButtonHint={t(
                'viewInvoices.projectSelectPopUp.continueHint'
              )}
              cancelButtonTitle=""
              showOkOption={true}
              showCancelOption={true}
              popupContent={
                <AurClientDropDownWrapper
                  sortColumn="Name"
                  routeName={location.pathname}
                  allowAutoComplete={false}
                  onClientSelection={() => setContinueButtonDisabled(false)}
                  buttonLabel={t('powerBiReports.clientDropdown.field')}
                />
              }
            />
          </Box>
        </>
      )}
      <AurLoadingIndicator
        overlay={true}
        isOpen={initialProjectListLoading || isReportLoading || isExporting}
      />
      <Box className="power-bi-report">
        {reportEmbedConfig ? (
          <div style={{height: `${reportHeight}px`}}>
            <PowerBIEmbed
              key={reportEmbedKey}
              embedConfig={reportEmbedConfig}
              cssClassName="powerbi-container"
              eventHandlers={eventHandlers}
              getEmbeddedComponent={(embeddedReport) => {
                reportRef.current = embeddedReport as Report;
                calculateReportHeight();
              }}
            />
          </div>
        ) : (
          <Box className="dashboard-container">
            <Box className="dashboard-empty-state">
              <AurSvgIcon src="images/empty-state-icon.svg" />
              <Typography className="typo-text headline3_size headline-color">
                {t(emptyStateMessage)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PowerBiReportsDashboard;